/**
 * Markdown Display
 * Renders a markdown string with formatting (bold, italics, headings, lists, links, code).
 * Backward-compatible with plain text — strings without markdown syntax just render as paragraphs.
 *
 * Used for completion notes / pre-site notes so techs can paste formatted Claude output
 * (use Claude.ai's "Copy" button to get clean markdown).
 *
 * Optional: highlight @mentions in text content (used for deal notes).
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface Props {
  content: string;
  className?: string;
  /** Smaller text variant for tight UI like job cards / timeline */
  compact?: boolean;
  /** Highlight @mentions inside text content with brand-secondary colour */
  highlightMentions?: boolean;
  /**
   * Render for a hardcoded-LIGHT surface (the tech app's white cards).
   * The default heading/strong colours use theme variables, which resolve
   * near-white when the app is in dark mode — invisible on a white card.
   * This pins them to fixed grays instead.
   */
  lightSurface?: boolean;
}

/** Recursively walk react children and split string nodes on @mentions, wrapping each in a styled span. */
function applyMentionHighlight(node: React.ReactNode): React.ReactNode {
  if (typeof node === 'string') {
    if (!node.includes('@')) return node;
    const parts = node.split(/(@\w+)/g);
    return parts.map((part, i) =>
      part.startsWith('@')
        ? (
          <span key={i} className="text-brand-secondary font-medium">
            {part}
          </span>
        )
        : part
    );
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => (
      <React.Fragment key={i}>{applyMentionHighlight(child)}</React.Fragment>
    ));
  }
  return node;
}

export function MarkdownDisplay({
  content,
  className = '',
  compact = false,
  highlightMentions = false,
  lightSurface = false,
}: Props) {
  if (!content) return null;

  const text = compact ? 'text-xs' : 'text-sm';
  const space = compact ? 'space-y-1.5' : 'space-y-2';
  // Emphasis colour: theme-aware by default; fixed dark gray on surfaces
  // that are white regardless of app theme (tech cards).
  const strongText = lightSurface ? 'text-gray-900' : 'text-theme-primary';

  // Wrap children with mention-highlighter when enabled
  const wrap = highlightMentions
    ? (children: React.ReactNode) => applyMentionHighlight(children)
    : (children: React.ReactNode) => children;

  return (
    <div className={`${text} ${space} leading-relaxed text-current ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="whitespace-pre-wrap">{wrap(children)}</p>,
          h1: ({ children }) => <h1 className={`text-lg font-bold mt-3 mb-1.5 first:mt-0 ${strongText}`}>{wrap(children)}</h1>,
          h2: ({ children }) => <h2 className={`text-base font-bold mt-3 mb-1.5 first:mt-0 pb-1 border-b border-theme-soft ${strongText}`}>{wrap(children)}</h2>,
          h3: ({ children }) => <h3 className={`text-sm font-bold mt-2.5 mb-1 first:mt-0 ${strongText}`}>{wrap(children)}</h3>,
          h4: ({ children }) => <h4 className={`text-sm font-semibold mt-1.5 mb-0.5 first:mt-0 ${strongText}`}>{wrap(children)}</h4>,
          strong: ({ children }) => <strong className={`font-semibold ${strongText}`}>{wrap(children)}</strong>,
          em: ({ children }) => <em className="italic">{wrap(children)}</em>,
          del: ({ children }) => <del className="line-through opacity-70">{wrap(children)}</del>,
          ul: ({ children }) => <ul className="list-disc pl-5 space-y-0.5 marker:text-theme-muted">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 space-y-0.5 marker:text-theme-muted">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{wrap(children)}</li>,
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 underline hover:text-blue-700"
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="bg-surface-hover px-1 py-0.5 rounded text-[0.85em] font-mono">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="bg-surface-hover border border-theme p-2 rounded-lg overflow-x-auto text-[0.85em] font-mono">
              {children}
            </pre>
          ),
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-brand-secondary/40 bg-surface-hover pl-3 py-0.5 rounded-r italic opacity-90">
              {wrap(children)}
            </blockquote>
          ),
          hr: () => <hr className="border-theme my-3" />,
          // GFM tables — the big win for pasted Claude output. Wrapped so wide
          // tables scroll horizontally inside narrow cards instead of overflowing.
          table: ({ children }) => (
            <div className="my-2 w-full max-w-full overflow-x-auto rounded-lg border border-theme">
              <table className="w-full border-collapse text-left">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-surface-raised border-b border-theme">{children}</thead>
          ),
          tbody: ({ children }) => (
            <tbody className="[&>tr:nth-child(even)]:bg-surface-hover">{children}</tbody>
          ),
          tr: ({ children }) => (
            <tr className="border-b border-theme-soft last:border-0">{children}</tr>
          ),
          th: ({ children }) => (
            <th className={`${compact ? 'px-2 py-1' : 'px-3 py-2'} text-[11px] font-semibold uppercase tracking-wide text-theme-primary whitespace-nowrap`}>
              {wrap(children)}
            </th>
          ),
          td: ({ children }) => (
            <td className={`${compact ? 'px-2 py-1 text-[11px]' : 'px-3 py-2 text-[0.9em]'} align-top text-theme-secondary`}>
              {wrap(children)}
            </td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
